import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faArrowRight, faBarcode, faFileLines, faHashtag, faLocationDot, faXmark } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { forkJoin, Observable } from 'rxjs';
import { RoomService } from '../../../../../core/services/storage/room-service';
import { WarehouseService } from '../../../../../core/services/storage/warehouse-service';
import { ScanPopupComponent } from '../../../../laboratory/pages/lot-summary/scan-popup/scan-popup.component';
import { lotInventory, lotInventoryService } from '../../../../laboratory/services/lab-quantity-service';
import { InventoryService } from '../../../../pick-plan/services/inventory.service';
import { InventoryLotWithHUM } from '../../../models/inventory-lot';
import { ReasonCode } from '../../../models/reason-code';

export type LotInventoryAction = 'transfer' | 'add' | 'remove' | 'addToOrder';

export interface InventoryLabPopupData {
  lot: InventoryLotWithHUM;
  isPopup: boolean;
  lotId?: number;
  type?: LotInventoryAction | string;
  lotName?: string;
}

const TITLES: Record<LotInventoryAction, string> = {
  transfer: 'Move Inventory',
  add: 'Add Inventory',
  remove: 'Remove Inventory',
  addToOrder: 'Add to Order',
};

/** Moves, adds, removes or orders a quantity of a lot. Closes with 'reload' once saved. */
@Component({
  selector: 'lot-details-by-room-move-inventory',
  templateUrl: './lot-details-by-room-move-inventory.component.html',
  styleUrls: ['./lot-details-by-room-move-inventory.component.scss']
})
export class LotDetailsByRoomMoveInventoryComponent implements OnInit {

  static open(dialog: MatDialog, data: InventoryLabPopupData): MatDialogRef<LotDetailsByRoomMoveInventoryComponent, 'reload'> {
    return dialog.open(LotDetailsByRoomMoveInventoryComponent, { data, width: '760px', maxWidth: '95vw' });
  }

  public faArrowRight = faArrowRight;
  public faBarcode = faBarcode;
  public faFileLines = faFileLines;
  public faHashtag = faHashtag;
  public faLocationDot = faLocationDot;
  public faXmark = faXmark;

  public warehouses = [];
  public rooms = [];
  public locations = [];
  public allWarehouses = [];
  public allRooms = [];
  public allLocations = [];
  public reasonCodes: ReasonCode[] = [];
  public isLoading = true;

  public lotInventoryObj: lotInventory = {
    lotId: this.data.lotId,
    handlingUnitMasterId: this.data.lot.handlingUnitMasterId,
    handlingUnitDetailId: this.data.lot.handlingUnitDetailId,
    quantity: null,
    toLocationId: null,
    toWarehouseId: null,
    toRoomId: null,
    reasonCodeId: null,
    orderNumber: null,
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InventoryLabPopupData,
    private dialogRef: MatDialogRef<LotDetailsByRoomMoveInventoryComponent, 'reload'>,
    private inventoryService: InventoryService,
    private lotInventoryService: lotInventoryService,
    private changeDetRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private warehouseService: WarehouseService,
    private roomService: RoomService,
    private toastrService: NbToastrService,
  ) { }

  ngOnInit() {
    forkJoin([
      this.inventoryService.getReasonCodes(),
      this.warehouseService.getWarehouses(),
      this.roomService.getRooms(),
      this.roomService.getRoomLocations(),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([reasonCodes, warehouses, rooms, locations]) => {
        this.reasonCodes = reasonCodes ?? [];
        this.allWarehouses = this.warehouses = warehouses ?? [];
        this.allRooms = this.rooms = rooms ?? [];
        this.allLocations = this.locations = locations ?? [];
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }, error: err => {
        this.toastrService.warning(err?.error, 'ERROR!');
        this.isLoading = false;
      }
    });
  }

  get title(): string {
    return TITLES[this.data.type as LotInventoryAction] ?? 'Move Inventory';
  }

  get isTransfer(): boolean {
    return this.data.type === 'transfer';
  }

  get isAddToOrder(): boolean {
    return this.data.type === 'addToOrder';
  }

  get canSave(): boolean {
    const obj = this.lotInventoryObj;
    if (!obj.quantity || obj.quantity <= 0 || this.isLoading)
      return false;
    return this.isAddToOrder ? !!obj.orderNumber : !!obj.reasonCodeId;
  }

  submit() {
    if (!this.canSave) return;

    const requests: Record<LotInventoryAction, () => Observable<lotInventory>> = {
      add: () => this.lotInventoryService.lotInventoryAdd(this.lotInventoryObj),
      remove: () => this.lotInventoryService.lotInventoryRemove(this.lotInventoryObj),
      transfer: () => this.lotInventoryService.lotInventoryTransfer(this.lotInventoryObj),
      addToOrder: () => this.lotInventoryService.lotInventoryAddToOrder(this.lotInventoryObj),
    };
    const request = requests[this.data.type as LotInventoryAction];
    if (!request) return;

    this.isLoading = true;
    request().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dialogRef.close('reload'),
      error: err => {
        this.toastrService.warning(this.isAddToOrder ? err?.error?.Message : err?.error, 'ERROR!');
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      }
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  roomSelectionChanged() {
    const room = this.allRooms.find(x => x.id == this.lotInventoryObj.toRoomId);
    if (!room) return;

    this.lotInventoryObj.toWarehouseId = room.warehouseId;
    this.locations = this.allLocations.filter(x => x.roomId == room.id);
    this.lotInventoryObj.toLocationId = null;
    this.changeDetRef.detectChanges();
  }

  warehouseSelectionChanged() {
    const warehouse = this.allWarehouses.find(x => x.id == this.lotInventoryObj.toWarehouseId);
    if (!warehouse) return;

    this.rooms = this.allRooms.filter(x => x.warehouseId == warehouse.id);
    this.locations = this.allLocations.filter(x => this.rooms.some(room => room.id == x.roomId));
    this.lotInventoryObj.toRoomId = null;
    this.lotInventoryObj.toLocationId = null;
    this.changeDetRef.detectChanges();
  }

  locationSelectionChanged() {
    const location = this.allLocations.find(x => x.id == this.lotInventoryObj.toLocationId);
    const room = this.allRooms.find(x => x.id == location?.roomId);
    if (!room) return;

    this.lotInventoryObj.toWarehouseId = room.warehouseId;
    this.lotInventoryObj.toRoomId = room.id;
    this.locations = this.allLocations.filter(x => x.roomId == room.id);
    this.changeDetRef.detectChanges();
  }

  scan() {
    this.dialog.open(ScanPopupComponent, { minWidth: '30%', data: { isLocationScan: true } })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(x => {
        if (!x) return;

        if (x.isLocation) {
          this.lotInventoryObj.toLocationId = x.location.id;
          this.locationSelectionChanged();
        }

        if (x.isRoom) {
          this.lotInventoryObj.toRoomId = x.room.id;
          this.roomSelectionChanged();
        }

        if (x.isWarehouse) {
          this.lotInventoryObj.toWarehouseId = x.warehouse.id;
          this.warehouseSelectionChanged();
        }

        this.changeDetRef.markForCheck();
      });
  }
}
